import React, { useEffect, useState } from 'react'
import classnames from 'classnames'
import Caret from 'components/Caret/Caret'
import { FilterProp, FilterPropForExtendedSubjectTree } from 'frontend/types'
import { Direction, KeyCodes } from 'frontend/enums'
import styles from './ExtendedFilterDropdown.module.css'
import useClickOutside from 'frontend/utils/hooks/useClickOutside'
import useKeys from 'frontend/utils/hooks/useKeys'
import { getKebabCaseOptionId } from 'frontend/utils/helpers'

interface FilterDropdownItemProps {
  activeLabels: string[]
  option: FilterPropForExtendedSubjectTree
  onOptionSelect: (option: FilterProp, optionsToRemove?: FilterProp[]) => void
  isOpen: boolean
  index: number
}

const ExtendedFilterDropdownItem = ({
  activeLabels,
  option,
  onOptionSelect,
  index,
  isOpen,
}: FilterDropdownItemProps) => {
  const [isSubjectExpanded, setIsSubjectExpanded] = useState(false)

  const isMainSubjectSelected = activeLabels.includes(option.mainSubject.label)

  useEffect(() => {
    if (!option.mainSubject.available) {
      setIsSubjectExpanded(false)
    }
  }, [option.mainSubject.available])

  return (
    <>
      <button
        className={styles.subjectExpanderButton}
        onClick={() => setIsSubjectExpanded(prev => !prev)}
        aria-disabled={!option.mainSubject.available}
        aria-expanded={isSubjectExpanded}
        disabled={!option.mainSubject.available}
        tabIndex={isOpen ? 0 : -1}
      >
        <span>{option.mainSubject.label}</span>
        <Caret
          direction={isSubjectExpanded ? Direction.Down : Direction.Right}
          styling={classnames(styles.smallCaret, {
            [`${styles.hidden}`]: !option.mainSubject.available,
          })}
        />
      </button>
      <ul
        className={classnames(styles.subSubjectList, {
          [`${styles.subjectExpanded}`]: isSubjectExpanded,
        })}
        aria-hidden={!isSubjectExpanded}
        data-testid={`sub-subject-list-${option.mainSubject.label}`}
      >
        <li className={styles.checkBox} key={option.mainSubject.value}>
          <input
            id={getKebabCaseOptionId(index, option.mainSubject.value)}
            name={getKebabCaseOptionId(index, option.mainSubject.value)}
            value={option.mainSubject.value}
            type="checkbox"
            checked={isMainSubjectSelected}
            onChange={() =>
              onOptionSelect(
                option.mainSubject,
                option.subSubjects.filter(opt =>
                  activeLabels.includes(opt.label),
                ),
              )
            }
            aria-disabled={!option.mainSubject.available}
            disabled={!option.mainSubject.available && !isMainSubjectSelected}
            tabIndex={isOpen ? 0 : -1}
          />
          <label
            htmlFor={getKebabCaseOptionId(index, option.mainSubject.value)}
            data-testid="filter-dropdown-option"
          >
            {`Allt inom ${option.mainSubject.label.toLowerCase()}`}{' '}
            {option.mainSubject.count &&
              option.mainSubject.count > 0 &&
              ` (${option.mainSubject.count})`}
          </label>
        </li>
        {option.subSubjects.map(subSubject => {
          const isSelected = activeLabels.includes(subSubject.label)
          return (
            <li className={styles.checkBox} key={subSubject.value}>
              <input
                id={getKebabCaseOptionId(index, subSubject.value)}
                name={getKebabCaseOptionId(index, subSubject.value)}
                value={subSubject.value as string}
                type="checkbox"
                checked={isSelected}
                onChange={() => onOptionSelect(subSubject)}
                aria-disabled={isMainSubjectSelected || !subSubject.available}
                disabled={
                  isMainSubjectSelected ||
                  (!subSubject.available && !isSelected)
                }
                tabIndex={isOpen ? 0 : -1}
              />
              <label
                htmlFor={getKebabCaseOptionId(index, subSubject.value)}
                data-testid="filter-dropdown-option"
              >
                {subSubject.label}{' '}
                {subSubject.count &&
                  subSubject.count > 0 &&
                  ` (${subSubject.count})`}
              </label>
            </li>
          )
        })}
      </ul>
    </>
  )
}

interface FilterDropdownProps {
  activeLabels?: string[]
  label: string
  onOptionSelect: (option: FilterProp, optionsToRemove?: FilterProp[]) => void
  options: FilterPropForExtendedSubjectTree[]
}

const ExtendedFilterDropdown = ({
  activeLabels,
  label,
  onOptionSelect,
  options,
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const caretDirection = isOpen ? Direction.Up : Direction.Down

  const insideRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false))

  useKeys(isOpen, {
    [KeyCodes.ESCAPE_KEY]: () => setIsOpen(false),
  })

  return (
    <div className={styles.panelContainer} ref={insideRef}>
      <button
        aria-expanded={isOpen}
        className={styles.button}
        data-testid="toggle-button"
        onClick={() => setIsOpen(prevState => !prevState)}
        type="button"
      >
        <span className={styles.label}>{label}</span>
        <Caret direction={caretDirection} styling={styles.caret} />
      </button>
      <div
        className={classnames(styles.optionsWrapper, {
          [`${styles.expanded}`]: isOpen,
        })}
      >
        <div
          className={classnames(styles.optionsContainer, {
            [`${styles.expanded}`]: isOpen,
            [`${styles.collapsed}`]: !isOpen,
          })}
        >
          {options.map((option, index) => {
            return (
              <ExtendedFilterDropdownItem
                index={index}
                key={index}
                activeLabels={activeLabels || []}
                option={option}
                onOptionSelect={onOptionSelect}
                isOpen={isOpen}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ExtendedFilterDropdown
